import {
  StringDevtoolDefinition,
  StringDevtoolState,
} from "../../../models/devtools/StringDevtool";
import { DEVTOOLS_CHEVRON_ICON, DEVTOOLS_MAIN_ICON } from "./StringDevtoolsIcons";
import { buildDevtoolsThemeBlock, ensureStringDevtoolsSharedStyles } from "./StringDevStyleSystem";

interface DockEntry {
  definition: StringDevtoolDefinition;
  button: HTMLButtonElement;
  unsubscribe: (() => void) | null;
}

interface DockPreferences {
  collapsed: boolean;
  active: Record<string, boolean>;
}

const DEVTOOLS_DOCK_STORAGE_KEY = "string-devtools:dock";

export class StringDevtoolsDock {
  private root: HTMLDivElement;
  private mainButton: HTMLButtonElement;
  private itemsWrap: HTMLDivElement;
  private fpsElement: HTMLDivElement;
  private entries: Map<string, DockEntry> = new Map();
  private collapsed: boolean = false;
  private readonly preferences: DockPreferences;

  constructor() {
    ensureStringDevtoolsSharedStyles();
    this.preferences = this.loadPreferences();
    this.injectStyles();
    this.root = document.createElement("div");
    this.root.setAttribute("data-string-devtools-dock", "");
    this.root.setAttribute("data-string-devtools-theme", "");
    this.root.setAttribute("data-collapsed", this.preferences.collapsed ? "true" : "false");
    this.mainButton = this.createMainButton();
    this.itemsWrap = document.createElement("div");
    this.itemsWrap.setAttribute("data-string-devtools-items", "");
    this.fpsElement = document.createElement("div");
    this.fpsElement.setAttribute("data-string-devtools-fps", "");
    this.fpsElement.title = "Current FPS";
    this.fpsElement.textContent = "0";
    this.root.appendChild(this.mainButton);
    this.itemsWrap.appendChild(this.fpsElement);
    this.root.appendChild(this.itemsWrap);
    (document.body ?? document.documentElement).appendChild(this.root);
    this.setCollapsed(this.preferences.collapsed, false);
  }

  add(definition: StringDevtoolDefinition): void {
    if (this.entries.has(definition.id)) {
      return;
    }

    this.applyStoredActiveState(definition);

    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("data-string-devtools-item", "");
    button.setAttribute("data-string-devtools-icon-button", "");
    button.setAttribute("data-devtool-id", definition.id);
    button.setAttribute("aria-label", definition.label);
    button.innerHTML = definition.icon;
    button.title = definition.label;
    button.addEventListener("click", () => {
      const nextActive = !definition.getState().active;
      definition.setActive(nextActive);
      if (!definition.subscribe) {
        const nextState = definition.getState();
        this.renderButton(button, definition.label, nextState);
        this.persistActiveState(definition.id, nextState.active);
      }
    });

    const unsubscribe = definition.subscribe
      ? definition.subscribe((state) => {
          this.renderButton(button, definition.label, state);
          this.persistActiveState(definition.id, state.active);
        })
      : null;

    this.entries.set(definition.id, { definition, button, unsubscribe });
    this.itemsWrap.appendChild(button);
    this.sortButtons();
    const currentState = definition.getState();
    this.renderButton(button, definition.label, currentState);
    this.persistActiveState(definition.id, currentState.active);
  }

  remove(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;

    entry.unsubscribe?.();
    entry.button.remove();
    this.entries.delete(id);
  }

  destroy(): void {
    for (const entry of this.entries.values()) {
      entry.unsubscribe?.();
      entry.button.remove();
    }
    this.entries.clear();
    this.root.remove();
  }

  setFPS(fps: number): void {
    this.fpsElement.textContent = String(Math.max(0, Math.round(fps)));
  }

  private sortButtons(): void {
    const ordered = Array.from(this.entries.values()).sort((left, right) => {
      const leftOrder = left.definition.order ?? 0;
      const rightOrder = right.definition.order ?? 0;
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      return left.definition.label.localeCompare(right.definition.label);
    });

    for (const entry of ordered) {
      this.itemsWrap.appendChild(entry.button);
    }
    this.itemsWrap.appendChild(this.fpsElement);
  }

  private createMainButton(): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("data-string-devtools-main", "");
    button.setAttribute("data-string-devtools-icon-button", "");
    button.setAttribute("aria-label", "Collapse developer tools");
    button.innerHTML = `
      <span data-string-devtools-main-icon="">${DEVTOOLS_MAIN_ICON}</span>
      <span data-string-devtools-main-chevron="">${DEVTOOLS_CHEVRON_ICON}</span>
    `;
    button.addEventListener("click", () => {
      this.setCollapsed(!this.collapsed);
    });
    return button;
  }

  private setCollapsed(collapsed: boolean, persist: boolean = true): void {
    this.collapsed = collapsed;
    this.root.setAttribute("data-collapsed", collapsed ? "true" : "false");
    this.mainButton.setAttribute(
      "aria-label",
      collapsed ? "Expand developer tools" : "Collapse developer tools",
    );
    if (persist) {
      this.preferences.collapsed = collapsed;
      this.savePreferences();
    }
  }

  private renderButton(button: HTMLButtonElement, label: string, state: StringDevtoolState): void {
    button.setAttribute("data-active", state.active ? "true" : "false");
    button.title = `${label}: ${state.active ? "On" : "Off"}`;
  }

  private applyStoredActiveState(definition: StringDevtoolDefinition): void {
    const saved = this.preferences.active[definition.id];
    if (typeof saved !== "boolean" || saved === definition.getState().active) {
      return;
    }

    definition.setActive(saved);
  }

  private persistActiveState(id: string, active: boolean): void {
    this.preferences.active[id] = active;
    this.savePreferences();
  }

  private loadPreferences(): DockPreferences {
    try {
      const raw = localStorage.getItem(DEVTOOLS_DOCK_STORAGE_KEY);
      if (!raw) {
        return { collapsed: false, active: {} };
      }

      const parsed = JSON.parse(raw) as Partial<DockPreferences>;
      return {
        collapsed: parsed.collapsed === true,
        active: parsed.active && typeof parsed.active === "object" ? parsed.active : {},
      };
    } catch {
      return { collapsed: false, active: {} };
    }
  }

  private savePreferences(): void {
    try {
      localStorage.setItem(DEVTOOLS_DOCK_STORAGE_KEY, JSON.stringify(this.preferences));
    } catch {
      // localStorage may be unavailable
    }
  }

  private injectStyles(): void {
    const styleId = "string-devtools-dock-styles";
    if (document.getElementById(styleId)) {
      return;
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      ${buildDevtoolsThemeBlock("[data-string-devtools-dock]")}

      [data-string-devtools-dock] {
        font-family: var(--string-dev-panel-font-family);
        --string-devtools-dock-width: 40px;
        --string-devtools-main-size: 30px;
        --string-devtools-top-padding: 5px;
        --string-devtools-bottom-padding: 5px;
        position: fixed;
        left: 24px;
        top: calc(50% - (var(--string-devtools-top-padding) + (var(--string-devtools-main-size) / 2)));
        z-index: 10030;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: var(--string-devtools-dock-width);
        min-height: 40px;
        padding: var(--string-devtools-top-padding) 0 var(--string-devtools-bottom-padding);
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 1) , 0 12px 16px 0 rgba(0, 0, 0, 0.05);
        border-radius: 16px;
        background-color: rgba(255, 255, 255, 1.0);

        opacity: 0.5;

        transition:
          opacity 0.3s ease,
          box-shadow 0.3s ease;
      }

      [data-string-devtools-dock]:hover {
        opacity: 1
      }

      [data-string-devtools-main] {
        width: var(--string-devtools-main-size);
        height: var(--string-devtools-main-size);
        padding: 0;
        border: 0;
        background: transparent;
        color: #101214;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        position: relative;
        cursor: pointer;
      }

      [data-string-devtools-main] svg {
        width: 22px;
        height: 24px;
      }

      [data-string-devtools-main-icon],
      [data-string-devtools-main-chevron] {
        position: absolute;
        inset: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: opacity 0.16s ease, transform 0.16s ease, color 0.16s ease;
      }

      [data-string-devtools-main-icon] {
        opacity: 1;
        transform: scale(1);
      }

      [data-string-devtools-main-chevron] {
        opacity: 0;
        color: #ff5b4d;
        transform: scale(0.8) translateY(1px);
      }

      [data-string-devtools-main-chevron] svg {
        width: 12px;
        height: 12px;
      }

      [data-string-devtools-dock]:not([data-collapsed="true"]) [data-string-devtools-main]:hover [data-string-devtools-main-icon] {
        opacity: 0;
        transform: scale(0.82);
      }

      [data-string-devtools-dock]:not([data-collapsed="true"]) [data-string-devtools-main]:hover [data-string-devtools-main-chevron] {
        opacity: 1;
        transform: scale(1);
      }

      [data-string-devtools-dock][data-collapsed="true"] [data-string-devtools-main-chevron] {
        transform: rotate(180deg) scale(0.8) translateY(-1px);
      }

      [data-string-devtools-dock][data-collapsed="true"] [data-string-devtools-main]:hover [data-string-devtools-main-icon] {
        opacity: 0;
        transform: scale(0.82);
      }

      [data-string-devtools-dock][data-collapsed="true"] [data-string-devtools-main]:hover [data-string-devtools-main-chevron] {
        opacity: 1;
        transform: rotate(180deg) scale(1);
      }

      [data-string-devtools-items] {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        overflow: hidden;
        max-height: 160px;
        opacity: 1;
        transform: translateY(0);
        transition:
          margin-top 0.22s ease,
          max-height 0.22s ease,
          opacity 0.16s ease,
          transform 0.22s ease;
      }

      [data-string-devtools-dock][data-collapsed="true"] [data-string-devtools-items] {
        margin-top: 0;
        max-height: 0;
        opacity: 0;
        transform: translateY(-6px);
        pointer-events: none;
      }

      [data-string-devtools-item] {
        width: 30px;
        height: 30px;
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        
        background-color: #ffffff;
        border-radius: 8px;
        border: 1px solid transparent;

        color: rgba(17, 17, 17, 0.25);
        cursor: pointer;
        transition:
          transform 0.15s ease,
          background 0.15s ease,
          color 0.15s ease,
          border-color 0.15s ease,
          box-shadow 0.15s ease;
      }

      [data-string-devtools-item] svg {
        width: 20px;
        height: 20px;
      }

      [data-string-devtools-item]:hover {
      
      }

      [data-string-devtools-item][data-active="true"] {
        border: 1px solid rgba(255, 255, 255, 1);
        background-color: rgba(221, 221, 221, 0.4);
        color: #111111;
        
      }

      [data-string-devtools-item][data-active="true"]:hover {
        background: rgba(17, 17, 17, 0.1);
      }

      [data-string-devtools-fps] {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        
        width: 30px;
        height: 30px;

        color: #111111;
        font-size: 16px;
      }

      @media (max-width: 768px) {
        [data-string-devtools-dock] {
          --string-devtools-dock-width: 66px;
          --string-devtools-top-padding: 10px;
          --string-devtools-bottom-padding: 12px;
          left: 12px;
        }

        [data-string-devtools-item] {
          width: 44px;
          height: 44px;
        }

        [data-string-devtools-fps] {
          font-size: 20px;
        }
      }
    `;

    document.head.appendChild(style);
  }
}
